/**
 * sequencer — "is this async continuation still the current one?"
 *
 * The hazard it exists for: an async handler awaits a worker round-trip and
 * then writes to shared, long-lived state (the singleton grid store, a Y.Doc).
 * If the user cancelled or started a newer operation while the await was in
 * flight, that write lands on state that now belongs to something else. In
 * this app the concrete case was version preview: a late snapshot import
 * mirrored an OLD version's layouts into the LIVE draft's doc and persisted
 * them — silent data loss (see EditorScreen.enterPreview).
 *
 * `start()` claims a ticket and returns a predicate for the caller to re-check
 * after EVERY await, not just the first. `invalidate()` retires whatever is
 * in flight without starting anything — that's cancellation.
 *
 * Deliberately not an AbortController: nothing here can actually abort a
 * postMessage already sent to a worker. The honest model is "let it finish,
 * then ignore it", and this names that precisely.
 */
export interface Sequencer {
  /** Claim the latest ticket; the returned predicate is true until superseded. */
  start(): () => boolean;
  /** Retire the in-flight ticket (cancellation) without claiming a new one. */
  invalidate(): void;
}

export function createSequencer(): Sequencer {
  let current = 0;
  return {
    start() {
      const mine = ++current;
      return () => mine === current;
    },
    invalidate() {
      current++;
    },
  };
}
